import {
	MutableRefObject,
	PropsWithChildren,
	ReactNode,
	Ref,
	createContext,
	useCallback,
	useContext,
	useState,
} from 'react';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';

import { Breakpoint, printUnclip } from '../helpers';
import { NoPrintWrapper } from './NoPrintWrapper';

const NAV_WIDTH = 240;

const Page = styled.div`
	display: flex;
	flex-direction: column;
	height: 100vh;
	height: 100dvh;
	width: 100%;
	min-width: 0;
	overflow: hidden;
	background: ${(props) => props.theme.tokens.surface.secondary};

	${printUnclip}

	@media print {
		background: ${(props) => props.theme.tokens.surface.white};
	}
`;

const Body = styled(Flex)`
	align-items: stretch;
	flex: 1 1 auto;
	min-width: 0;
	min-height: 0;
	width: 100%;

	${printUnclip}
`;

const Nav = styled(Box)`
	box-sizing: border-box;
	flex-direction: column;
	align-self: stretch;
	min-height: 0;
	overflow: hidden;

	@media print {
		display: none !important;
	}
`;

const MainColumn = styled(Box)`
	flex-direction: column;
	flex: 1 1 0%;
	min-width: 0;
	min-height: 0;

	${printUnclip}
`;

const Main = styled.main`
	display: flex;
	flex-direction: column;
	flex-shrink: 1;
	flex-grow: 1;
	min-width: 0;
	min-height: 0;
	background: ${(props) => props.theme.tokens.surface.white};
	border-radius: ${(props) => props.theme.tokens.radius.xl}px;
	margin: 0 ${(props) => props.theme.tokens.gap.element}px
		${(props) => props.theme.tokens.gap.element}px 0;
	overflow: auto;
	border: 1px solid ${(props) => props.theme.tokens.border.subtle};

	@media screen and (max-width: ${Breakpoint.Medium}) {
		margin: 0;
		border-radius: 0;
		border: none;
	}

	${printUnclip}

	@media print {
		border-radius: 0;
		margin: 0;
		border: none;
	}
`;

const AppScrollContext = createContext<HTMLElement | null>(null);

/**
 * The document itself does not scroll under this layout, so anything that wants
 * to read or drive the scroll position has to go through the main element.
 */
export function useAppScrollElement() {
	return useContext(AppScrollContext);
}

export function AppLayout(
	props: PropsWithChildren<{
		banner?: ReactNode;
		nav: ReactNode;
		header?: ReactNode;
		navOpen?: boolean;
		mainRef?: Ref<HTMLElement>;
	}>,
) {
	const navOpen = props.navOpen ?? false;
	const { mainRef } = props;
	const [main, setMain] = useState<HTMLElement | null>(null);

	const attachMain = useCallback(
		(element: HTMLElement | null) => {
			setMain(element);

			if (typeof mainRef === 'function') {
				mainRef(element);
			} else if (mainRef) {
				(mainRef as MutableRefObject<HTMLElement | null>).current = element;
			}
		},
		[mainRef],
	);

	return (
		<AppScrollContext.Provider value={main}>
			<Page>
				{props.banner ? <NoPrintWrapper>{props.banner}</NoPrintWrapper> : null}
				<Body flexDirection={['column', 'column', 'row']}>
					<Nav
						display={[navOpen ? 'flex' : 'none', navOpen ? 'flex' : 'none', 'flex']}
						flex={['1 1 auto', '1 1 auto', `0 0 ${NAV_WIDTH}px`]}
						width={['100%', '100%', NAV_WIDTH]}
					>
						{props.nav}
					</Nav>
					<MainColumn display={[navOpen ? 'none' : 'flex', navOpen ? 'none' : 'flex', 'flex']}>
						{props.header ? <NoPrintWrapper>{props.header}</NoPrintWrapper> : null}
						<Main ref={attachMain}>{props.children}</Main>
					</MainColumn>
				</Body>
			</Page>
		</AppScrollContext.Provider>
	);
}
